import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AuthGuard } from '@/components/AuthGuard';
import { Toaster } from 'sonner';
import { LayoutDashboard, ShoppingBag, Plus, LogOut, Settings, ChevronRight, User, Moon, Sun, Menu, X, Shirt, Library, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

export function AdminLayout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light') {
      setIsDarkMode(false);
      document.documentElement.classList.remove('dark-mode');
      document.documentElement.classList.add('light-mode');
    } else {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark-mode');
      document.documentElement.classList.remove('light-mode');
    }
  }, []);

  // Close sidebar when navigating on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    if (!isDarkMode) {
      document.documentElement.classList.add('dark-mode');
      document.documentElement.classList.remove('light-mode');
      localStorage.setItem('theme', 'dark');
      toast.success('Dark mode enabled');
    } else {
      document.documentElement.classList.remove('dark-mode');
      document.documentElement.classList.add('light-mode');
      localStorage.setItem('theme', 'light');
      toast.success('Light mode enabled');
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <AuthGuard>
      <div className={`min-h-screen bg-background flex flex-col ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
        <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-16 items-center gap-2 px-4">
            <button onClick={toggleSidebar} className="-ml-2 rounded-md p-1.5 text-muted-foreground hover:bg-secondary/80 transition-colors lg:hidden">
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/admin" className="flex items-center gap-2 font-semibold">
              <div className="bg-primary rounded-md p-1.5 text-primary-foreground">
                <Library size={18} />
              </div>
              <span className="font-semibold text-xl">Replibrary Panel</span>
            </Link>
            <div className="flex items-center ml-auto gap-2">
              <div className="flex items-center gap-2 mr-2">
                <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="relative hover:bg-secondary/80 transition-colors">
                  {isDarkMode ? <Moon size={18} /> : <Sun size={18} />}
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden">
          <aside 
            className={`fixed inset-y-0 left-0 z-30 mt-16 overflow-y-auto bg-card border-r transition-all duration-300 
              ${isMobile ? (sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full') : 'translate-x-0 w-64 md:w-52 lg:w-64'}`}
          >
            <div className="py-4">
              <div className="px-4 py-2">
                <div className="flex items-center gap-2 py-3 mb-2 border-b border-border pb-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <User size={18} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium truncate">{user?.email}</span>
                    <span className="text-xs text-muted-foreground">Admin</span>
                  </div>
                </div>
              </div>
              <nav className="mt-2 px-2 space-y-1">
                <Link
                  to="/admin"
                  className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                    isActive('/admin')
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-primary/10'
                  }`}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <LayoutDashboard className="mr-3 h-5 w-5" />
                  <span className="whitespace-nowrap">Dashboard</span>
                  {isActive('/admin') && <ChevronRight className="ml-auto h-4 w-4 opacity-100 transition-opacity" />}
                </Link>

                <Link
                  to="/admin/clothinglist"
                  className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                    isActive('/admin/clothinglist')
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-primary/10'
                  }`}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <ShoppingBag className="mr-3 h-5 w-5" />
                  <span className="whitespace-nowrap">Products</span>
                  {isActive('/admin/clothinglist') && <ChevronRight className="ml-auto h-4 w-4 opacity-100 transition-opacity" />}
                </Link>

                <Link
                  to="/admin/clothinglist/new"
                  className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                    isActive('/admin/clothinglist/new')
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-primary/10'
                  }`}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <Plus className="mr-3 h-5 w-5" />
                  <span className="whitespace-nowrap">Add Product</span>
                  {isActive('/admin/clothinglist/new') && <ChevronRight className="ml-auto h-4 w-4 opacity-100 transition-opacity" />}
                </Link>
                
                <Link
                  to="/admin/creators"
                  className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                    isActive('/admin/creators') || isActive('/admin/creators/new') || location.pathname.includes('/admin/creators/')
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-primary/10'
                  }`}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <Users className="mr-3 h-5 w-5" />
                  <span className="whitespace-nowrap">Creators</span>
                  {(isActive('/admin/creators') || isActive('/admin/creators/new') || location.pathname.includes('/admin/creators/')) && 
                    <ChevronRight className="ml-auto h-4 w-4 opacity-100 transition-opacity" />
                  }
                </Link>
                
                <Link
                  to="/admin/settings"
                  className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                    isActive('/admin/settings')
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-primary/10'
                  }`}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <Settings className="mr-3 h-5 w-5" />
                  <span className="whitespace-nowrap">Settings</span>
                  {isActive('/admin/settings') && <ChevronRight className="ml-auto h-4 w-4 opacity-100 transition-opacity" />}
                </Link>
                
                <div className="pt-4 mt-4 border-t border-border">
                  <button
                    onClick={() => signOut()}
                    className="w-full group flex items-center px-3 py-2.5 text-sm font-medium rounded-md text-foreground hover:bg-destructive/10 transition-colors"
                  >
                    <LogOut className="mr-3 h-5 w-5" />
                    <span className="whitespace-nowrap">Sign Out</span>
                  </button>
                </div>
              </nav>
            </div>
          </aside>

          <main className={`flex-1 overflow-y-auto pt-16 transition-all duration-300 ${isMobile ? '' : 'md:ml-52 lg:ml-64'}`}>
            <div className="container mx-auto px-6 py-6">
              <Outlet />
            </div>
          </main>
        </div>
        <Toaster />
      </div>
    </AuthGuard>
  );
}
